<script setup lang="ts">
/**
 * 任務清單（`/jobs`；operator 的主戰場之一——升級／備份／診斷都在這裡看）。
 *
 * - 5 秒輪詢（`lib/poll.ts`：切到背景暫停、同時只跑一個請求）。
 * - **主機篩選送伺服器**（`?host_id=`，真的縮小結果集）；**狀態分類在前端**——後端的
 *   `?state=` 只吃單一值，而「進行中」是 queued／leased／running 三個狀態的聯集，
 *   而且分頁籤上的計數要一次算齊（同 `LicensesView` 的作法）。
 * - **客戶篩選也送伺服器**（`?customer_id=`；推導規則見 `HostJob.customer_id` 的註解）。
 *   ⚠️ 客戶下拉**只對 admin+ 顯示**——資料來源 `GET /customers` 是 `require_admin`，
 *   而本頁 operator 可見，少了這道 gate 就是一開頁 403 白屏。
 * - 取消＝admin 以上；重試依 kind 分級（operator 只能重試升級／備份／診斷）。
 * - **keyset 分頁**（批93-D）：後端回 `{items, next_cursor}`。這一頁刻意用**一頁 200 筆**
 *   （＝改分頁之前本頁固定送的 `limit`，第一次載入的內容與分頁籤計數一個字都沒變），
 *   超過才出現「載入更多」。★ 輪詢時會把**已載入的頁數**一起重抓——只抓第一頁的話，
 *   使用者每按一次「載入更多」都會在 5 秒後被打回原形。
 */
import { computed, onMounted, ref, watch } from 'vue'

import FilterClearButton from '@/components/FilterClearButton.vue'
import UiButton from '@/components/UiButton.vue'
import { ApiError } from '@/api/client'
import * as hosting from '@/api/hosting'
import type { HostJob, ManagedHost } from '@/api/hosting'
import {
  canRetryJob,
  fmtDuration,
  isActiveJobState,
  isCancelableJob,
  jobElapsed,
  jobKindLabel,
  jobPhaseLabel,
  jobStateClass,
  jobStateLabel,
} from '@/lib/jobs'
import { usePersistedFilters } from '@/lib/persistedFilters'
import { usePolling } from '@/lib/poll'
import { fmtSeen } from '@/lib/staleness'
import { useHostingRoute } from '@/lib/hostingRoutes'
import { customerOptionLabel } from '@/lib/customerLabel'
import { useAuthStore } from '@/stores/auth'
import { useDataStore } from '@/stores/data'
import { useDialogsStore } from '@/stores/dialogs'
import { vCardTable } from '@/lib/cardTable'

/**
 * 代管路由名的視角分流（批99-B）：我方員工＝`host-detail`，代理商＝`reseller-host-detail`。
 * 本頁兩個視角共用同一支元件，所以每個連結都得問一次自己現在是誰。
 */
const hr = useHostingRoute()

const auth = useAuthStore()
const store = useDataStore()
const dialogs = useDialogsStore()

/**
 * 取消／重試與客戶過濾的權限（批99-B）：我方 admin 以上，**或代理商**（限自家主機的
 * 任務——後端 `/reseller/jobs` 只列自家主機，別家的 job id 一律 404）。
 * `isAdminOrAbove` 對代理商恆為 false（rank 0），所以要顯式加上。
 */
const canManage = computed(() => auth.isAdminOrAbove || auth.isReseller)

const jobs = ref<HostJob[]>([])
const hosts = ref<ManagedHost[]>([])
const loading = ref(true)
const loadError = ref('')
// --- 狀態分類（前端；見檔頭）。常數要在 `usePersistedFilters` 之前宣告 ---
type Bucket = 'active' | 'succeeded' | 'failed' | 'canceled' | 'all'
const FILTERS: { key: Bucket; label: string }[] = [
  { key: 'active', label: '進行中' },
  { key: 'succeeded', label: '已完成' },
  { key: 'failed', label: '失敗／逾時' },
  { key: 'canceled', label: '已取消' },
  { key: 'all', label: '全部' },
]
// 批186：主機／客戶下拉與分類籤記住；要在 `usePolling` 之前（輪詢一註冊就立刻載入）。
const pf = usePersistedFilters(
  'jobs',
  { hostFilter: '', custFilter: '', filter: 'active' as Bucket },
  { oneOf: { filter: FILTERS.map((f) => f.key) } },
)
const { hostFilter, custFilter, filter } = pf.refs
const { dirty: filtersDirty } = pf
/** 下拉由下面的 `watch` 重載；分類籤只是前端分桶，改值即可。 */
const clearFilters = () => pf.clear()
/** 輪詢時一起更新的「現在」——耗時欄靠它，才不會每次 render 都自己跳。 */
const now = ref(Date.now())
/** 一頁幾筆（＝批93-D 之前本頁固定送的 limit，分頁籤計數因此完全沒變）。 */
const PAGE = 200
/** 輪詢重抓時最多重走幾頁——同時也是「載入更多」的上限（200×5＝1000 筆）。 */
const MAX_PAGES = 5
/** keyset 續頁游標（null＝已經載完）。 */
const nextCursor = ref<string | null>(null)
/** 目前載了幾頁（輪詢要把同樣多的頁重抓回來，見檔頭）。 */
const pagesLoaded = ref(1)
const loadingMore = ref(false)

function errText(e: unknown, fallback: string): string {
  const d = e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
  if (d == null) return fallback
  return typeof d === 'string' ? d : JSON.stringify(d)
}

function jobQuery(cursor: string | null) {
  return {
    hostId: hostFilter.value || undefined,
    customerId: custFilter.value || undefined,
    limit: PAGE,
    cursor,
  }
}

async function load() {
  try {
    // ★ 重抓**已載入的頁數**而不是只抓第一頁（見檔頭）。`next_cursor` 為 null 就收手，
    //   所以常態只會是一次往返。
    const acc: HostJob[] = []
    let cursor: string | null = null
    for (let i = 0; i < pagesLoaded.value; i++) {
      const page = await hosting.listHostJobs(jobQuery(cursor))
      acc.push(...page.items)
      cursor = page.next_cursor
      if (!cursor) break
    }
    jobs.value = acc
    nextCursor.value = cursor
    now.value = Date.now()
    loadError.value = ''
  } catch (e) {
    loadError.value = errText(e, '讀取任務清單失敗')
  } finally {
    loading.value = false
  }
}

async function loadMore() {
  if (!nextCursor.value || loadingMore.value) return
  loadingMore.value = true
  try {
    const page = await hosting.listHostJobs(jobQuery(nextCursor.value))
    jobs.value = [...jobs.value, ...page.items]
    nextCursor.value = page.next_cursor
    pagesLoaded.value += 1
  } catch (e) {
    loadError.value = errText(e, '讀取下一頁失敗')
  } finally {
    loadingMore.value = false
  }
}

const poll = usePolling(load, 5_000)
// 改過濾條件＝重新從第一頁開始（游標是跟著 WHERE 走的，換條件不能沿用舊游標）。
watch([hostFilter, custFilter], () => {
  pagesLoaded.value = 1
  nextCursor.value = null
  void poll.refresh()
})
void hosting
  .listManagedHosts()
  .then((rows) => {
    hosts.value = rows
  })
  .catch(() => {})
// ★ 客戶清單只在 admin+ 載（`GET /customers` 是 require_admin，見檔頭）。
onMounted(() => {
  if (canManage.value) void store.ensureCustomers().catch(() => {})
})

function bucketOf(job: HostJob): Exclude<Bucket, 'all'> {
  if (isActiveJobState(job.state)) return 'active'
  if (job.state === 'succeeded') return 'succeeded'
  if (job.state === 'canceled') return 'canceled'
  return 'failed' // failed／expired
}

const counts = computed(() => {
  const c: Record<Bucket, number> = {
    active: 0,
    succeeded: 0,
    failed: 0,
    canceled: 0,
    all: jobs.value.length,
  }
  for (const j of jobs.value) c[bucketOf(j)]++
  return c
})

const rows = computed(() =>
  jobs.value
    .filter((j) => filter.value === 'all' || bucketOf(j) === filter.value)
    .map((j) => ({
      job: j,
      active: isActiveJobState(j.state),
      elapsed: fmtDuration(jobElapsed(j, now.value)),
      // 批150：已被 agent 領走的回收不給取消（後端 409）
      canCancel: canManage.value && isCancelableJob(j),
      // 批150：回收／停站／復站／改站台設定不給重試（要回主機頁經專用流程重做）
      canRetry: canRetryJob(j, auth.roleRank),
    })),
)

// --- 動作 ---
const busyJob = ref<string | null>(null)

async function cancel(job: HostJob) {
  const ok = await dialogs.confirm({
    title: '取消任務',
    message: `取消「${jobKindLabel(job.kind)}」（${job.job_id}）？已經在跑的動作會在 agent 下次回報時中止，做到一半的步驟不會自動回復。`,
    danger: true,
    confirmText: '取消任務',
  })
  if (!ok) return
  busyJob.value = job.job_id
  try {
    await hosting.cancelHostJob(job.job_id)
    await poll.refresh()
  } catch (e) {
    await dialogs.notify({ title: '操作失敗', message: errText(e, '取消失敗') })
  } finally {
    busyJob.value = null
  }
}

async function retry(job: HostJob) {
  const ok = await dialogs.confirm({
    title: '重試任務',
    message: `重新派一次「${jobKindLabel(job.kind)}」？會建立新的一筆任務（舊那筆的紀錄與 log 都保留），參數沿用原本的。`,
    confirmText: '重試',
  })
  if (!ok) return
  busyJob.value = job.job_id
  try {
    const fresh = await hosting.retryHostJob(job.job_id)
    await poll.refresh()
    await dialogs.notify({ title: '已重新派工', message: `新任務編號 ${fresh.job_id}。` })
  } catch (e) {
    await dialogs.notify({ title: '操作失敗', message: errText(e, '重試失敗') })
  } finally {
    busyJob.value = null
  }
}
</script>

<template>
  <div class="page">
    <div class="page-head">
      <h1>任務</h1>
      <span class="count">{{ jobs.length }}</span>
      <span class="spacer" />
      <label v-if="canManage" class="hf">
        <span>客戶</span>
        <select v-model="custFilter">
          <option value="">全部客戶</option>
          <option v-for="c in store.customers" :key="c.id" :value="c.id">{{ customerOptionLabel(c, { ownership: !auth.isReseller }) }}</option>
        </select>
      </label>
      <label class="hf">
        <span>主機</span>
        <select v-model="hostFilter">
          <option value="">全部主機</option>
          <option v-for="h in hosts" :key="h.id" :value="h.id">{{ h.name }}</option>
        </select>
      </label>
      <FilterClearButton :dirty="filtersDirty" @click="clearFilters" />
    </div>

    <div v-if="loading" class="loading-wrap"><span class="spinner lg" /></div>

    <div v-else class="card">
      <p v-if="loadError" class="err">{{ loadError }}（顯示的是最後一次成功讀取的資料）</p>

      <div class="tabs filters" data-hscroll>
        <button
          v-for="f in FILTERS"
          :key="f.key"
          type="button"
          class="tab"
          :class="{ active: filter === f.key }"
          @click="filter = f.key"
        >
          {{ f.label }}<span class="tab-n">{{ counts[f.key] }}</span>
        </button>
      </div>

      <div v-if="!rows.length" class="empty">
        <div class="empty-title">這個分類目前沒有任務</div>
        <div class="empty-sub">
          升級／備份／診斷從主機詳情的站台列派工；開新站台走「開站精靈」。任務一旦派出就會出現在這裡。
        </div>
      </div>

      <div v-else class="tbl-scroll" data-hscroll>
        <table v-card-table class="tbl card-table">
          <thead>
            <tr>
              <th>任務</th><th>對象</th><th>客戶</th><th>狀態</th><th>階段 / 進度</th>
              <th>發起人</th><th>耗時</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in rows" :key="r.job.job_id">
              <td>
                <div class="cell2">
                  <RouterLink class="jname" :to="{ name: hr('job-detail'), params: { jobId: r.job.job_id } }">
                    {{ jobKindLabel(r.job.kind) }}
                  </RouterLink>
                  <span class="sub">
                    <code class="mono">{{ r.job.job_id }}</code>
                    <span v-if="r.job.signed" class="chip sig" title="帶 IPOJOB1 指令簽章（agent 離線驗章才執行）">已簽章</span>
                  </span>
                </div>
              </td>
              <td>
                <div class="cell2">
                  <RouterLink :to="{ name: hr('host-detail'), params: { id: r.job.host_id } }">
                    {{ r.job.host_name ?? r.job.host_id.slice(0, 8) }}
                  </RouterLink>
                  <span class="sub">{{ r.job.site_slug ?? '（主機層）' }}</span>
                </div>
              </td>
              <td class="nowrap">
                <template v-if="r.job.customer_name">
                  {{ r.job.customer_name }}
                  <!-- 主機層 job（site_id 為 null）走的是「獨佔 VM」推導，不是站台歸戶——標出來免得誤讀。 -->
                  <span
                    v-if="!r.job.site_id"
                    class="chip excl"
                    title="這是主機層動作；因為這台是該客戶獨佔的 VM，整台只服務他一家，所以歸給他"
                  >
                    獨佔 VM
                  </span>
                </template>
                <span
                  v-else
                  class="subtle"
                  title="多客戶共住的 VM 上的主機層動作，沒有單一客戶歸屬"
                >
                  —
                </span>
              </td>
              <td class="nowrap">
                <span class="status-pill" :class="jobStateClass(r.job.state)">{{ jobStateLabel(r.job.state) }}</span>
                <span v-if="r.job.attempts > 1" class="sub">第 {{ r.job.attempts }} 次嘗試</span>
              </td>
              <td class="phase">
                <div class="ph-label">{{ jobPhaseLabel(r.job.phase) }}</div>
                <div class="pbar"><span class="pfill" :style="{ width: `${r.job.progress}%` }" /></div>
                <div v-if="r.job.detail" class="sub" :title="r.job.detail">{{ r.job.detail }}</div>
                <div v-else-if="r.job.error" class="sub errtext" :title="r.job.error">{{ r.job.error }}</div>
              </td>
              <td class="nowrap">{{ r.job.requested_by ?? '—' }}</td>
              <td class="nowrap">
                {{ r.elapsed }}
                <span class="sub">{{ fmtSeen(r.job.created_at, { short: true }) }}</span>
              </td>
              <td class="acts-cell">
                <div class="acts">
                  <UiButton
                    v-if="r.canCancel"
                    size="sm"
                    :loading="busyJob === r.job.job_id"
                    @click="cancel(r.job)"
                  >
                    取消
                  </UiButton>
                  <UiButton
                    v-if="r.canRetry"
                    size="sm"
                    variant="primary"
                    :loading="busyJob === r.job.job_id"
                    @click="retry(r.job)"
                  >
                    重試
                  </UiButton>
                  <RouterLink class="tab detail-link" :to="{ name: hr('job-detail'), params: { jobId: r.job.job_id } }">
                    詳情
                  </RouterLink>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- keyset 分頁：一頁 200 筆。`next_cursor` 非 null 才畫這一列（形狀與 LicensesView 一致）。 -->
      <div v-if="nextCursor && pagesLoaded < MAX_PAGES" class="more">
        <UiButton size="sm" :loading="loadingMore" @click="loadMore">載入更多</UiButton>
        <span class="subtle">已載入 {{ jobs.length }} 筆</span>
      </div>
      <p v-else-if="nextCursor" class="more subtle">
        已載入 {{ jobs.length }} 筆（顯示上限）。更早的任務請用上方的主機／客戶過濾條件縮小範圍。
      </p>
    </div>
  </div>
</template>

<style scoped>
.page-head {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  margin-bottom: var(--sp-4);
}
.page-head h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.count {
  font-size: var(--fs-12);
  color: var(--fg-3);
  background: var(--bg);
  padding: 1px 8px;
  border-radius: 999px;
}
.spacer {
  flex: 1;
}
.hf {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  font-size: var(--fs-12);
  color: var(--fg-3);
  white-space: nowrap;
}
.err {
  margin: 0 0 var(--sp-3);
  color: var(--danger);
  font-size: var(--fs-12);
}
.filters {
  margin-bottom: var(--sp-3);
}
.tab-n {
  margin-left: 6px;
  font-size: var(--fs-11);
  font-weight: 500;
  color: var(--fg-4);
}
.tab.active .tab-n {
  color: inherit;
}
.cell2 {
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-start;
}
.jname,
td a {
  color: var(--brand-700);
  font-weight: 600;
  text-decoration: none;
}
[data-theme='dark'] .jname,
[data-theme='dark'] td a {
  color: var(--brand-400);
}
.jname:hover,
td a:hover {
  text-decoration: underline;
}
/* `.sub` 只給 td **內**的 span／div（display:block 套在 td 本身會讓儲存格脫離表格列）。 */
.sub {
  display: block;
  font-size: var(--fs-11);
  color: var(--fg-4);
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sub.errtext {
  color: var(--danger);
}
.chip.sig {
  margin-left: 4px;
  background: var(--st-transferred-bg);
  border-color: transparent;
  color: var(--st-transferred-fg);
}
.chip.excl {
  margin-left: 4px;
  font-size: var(--fs-11);
}
.phase {
  min-width: 200px;
}
.ph-label {
  font-size: var(--fs-12);
  color: var(--fg-2);
}
.pbar {
  height: 5px;
  margin: 3px 0;
  border-radius: 999px;
  background: var(--bg-sunken);
  overflow: hidden;
}
.pfill {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: var(--brand-500);
  transition: width var(--dur-base) var(--ease);
}
.nowrap {
  white-space: nowrap;
}
.detail-link {
  padding: 4px 10px;
  font-size: var(--fs-11);
}
/* keyset 分頁的「載入更多」列（形狀與 LicensesView／ResellerLicensesView 一致） */
.more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-2);
  padding: var(--sp-3);
  margin: 0;
  border-top: 1px solid var(--border);
}
/* 平板（批171）：進度條欄讓出寬度給右邊的發起人與耗時（不然被釘在右側的操作欄蓋住）。 */
@media (--compact) {
  .phase {
    min-width: 120px;
  }
}
</style>
